import type { BlockCandidate, Rect } from '@/types/ainotebook'
import { usePdfJs } from '@/composables/ainotebook/usePdfJs'
import { hasRealChars, isGhostTextBar, isInImageAreaLikelyNoise, isLikelyPdfNoise, isLongThinBar } from './noise'

export async function extractPdfTextBlocksViaTextLayer(
  page: any,
  viewport: any,
  canvasW: number,
  canvasH: number
): Promise<BlockCandidate[]> {
  const textContent = await page.getTextContent({
    normalizeWhitespace: true,
    disableCombineTextItems: false
  })

  const items = textContent?.items ?? []
  if (!items.length) return []

  const pdf = await usePdfJs()
  const Util = pdf.Util

  const pageW = canvasW
  const pageH = canvasH

  const raw: Array<{ text: string; rect: Rect }> = []

  for (const it of items) {
    const text = String(it.str || '').replace(/\s+/g, ' ').trim()
    if (!text) continue
    if (!hasRealChars(text)) continue

    const w0 = Math.max(0.1, Number(it.width ?? 0))
    const h0 = Math.max(0.1, Number(it.height ?? 1))

    const m = Util.transform(viewport.transform, it.transform)
    const bb = Util.getAxialAlignedBoundingBox([0, 0, w0, h0], m)

    let x0 = bb[0], y0 = bb[1], x1 = bb[2], y1 = bb[3]
    if (![x0, y0, x1, y1].every(Number.isFinite)) continue

    const x = Math.max(0, Math.min(x0, x1))
    const y = Math.max(0, Math.min(y0, y1))
    const w = Math.min(pageW - x, Math.abs(x1 - x0))
    const h = Math.min(pageH - y, Math.abs(y1 - y0))
    if (w < 2 || h < 2) continue

    const rect = { x, y, w, h }
    if (isGhostTextBar(text, rect, pageW, pageH)) continue

    raw.push({ text, rect })
  }

  // 你原本是「關閉合併」，這裡也保持：只做濾噪
  return raw
    .map(b => ({ text: b.text, rect: { ...b.rect } }))
    .filter(b => {
      if (!b.text || b.text.trim().length === 0) return false
      if (b.rect.w <= 8 || b.rect.h <= 6) return false
      if (isLongThinBar(b.rect, pageW, pageH)) return false
      if (isInImageAreaLikelyNoise(b.text, b.rect, pageH)) return false
      if (isLikelyPdfNoise(b.text, b.rect)) return false
      return true
    })
}
